import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

const interactedVariantPickers = new WeakSet();

class VariantDropdownComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox', 'summaryValue'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncSummaryValue();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncSummaryValue();
  }

  /**
   * Handles keyboard navigation for the custom dropdown.
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    const { details, listbox, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(listbox instanceof HTMLElement)) return;
    const actualTarget = event.composedPath?.()[0];

    const options = this.#options;
    if (!options.length) return;

    if (!details.open) {
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && actualTarget === summary) {
        event.preventDefault();
        details.open = true;
      }
      return;
    }

    const currentFocused = options.find((option) => option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;
      case 'Enter':
      case ' ':
        if (actualTarget instanceof Element) {
          const option = actualTarget.closest('[role="option"]');
          if (option instanceof HTMLElement) {
            event.preventDefault();
            this.#selectOption(option);
          }
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Syncs the open state of the dropdown.
   */
  handleToggle = () => {
    const { details, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    summary.setAttribute('aria-expanded', details.open.toString());

    if (details.open) {
      this.#selectedOption?.focus();
    }
  };

  /**
   * Updates the summary and selected states.
   * @param {Event} event
   */
  handleChange = (event) => {
    const actualTarget = event.composedPath?.()[0];
    if (!(actualTarget instanceof HTMLInputElement)) return;

    const option = actualTarget.closest('[role="option"]');
    if (!(option instanceof HTMLElement)) return;

    this.refs.summaryValue.textContent = actualTarget.dataset.optionLabel ?? actualTarget.value;

    this.#options.forEach((item) => {
      item.setAttribute('aria-selected', 'false');
      item.tabIndex = -1;
    });

    option.setAttribute('aria-selected', 'true');
    option.tabIndex = 0;
    this.#closeDropdown();
  };

  /**
   * @returns {HTMLElement[]}
   */
  get #options() {
    return Array.from(this.querySelectorAll('[role="option"]')).filter((option) => option instanceof HTMLElement);
  }

  /**
   * @returns {HTMLElement | undefined}
   */
  get #selectedOption() {
    return this.#options.find((option) => option.getAttribute('aria-selected') === 'true');
  }

  /**
   * @param {HTMLElement[]} options
   * @param {number} newIndex
   */
  #moveFocus(options, newIndex) {
    options.forEach((option) => {
      option.tabIndex = -1;
    });

    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * @param {HTMLElement} option
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.click();
  }

  #syncSummaryValue() {
    const { summaryValue } = this.refs;
    if (!(summaryValue instanceof HTMLElement)) return;

    if (summaryValue.dataset.preserveSelectedLabel === 'true') return;

    const defaultLabel = summaryValue.dataset.defaultLabel;
    if (!defaultLabel) return;

    const variantPicker = this.closest('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return;
    if (interactedVariantPickers.has(variantPicker)) return;

    summaryValue.textContent = defaultLabel;
  }

  #closeDropdown() {
    const { details, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = false;
    summary?.focus();
  }
}

if (!customElements.get('variant-dropdown-component')) {
  customElements.define('variant-dropdown-component', VariantDropdownComponent);
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Object | null} */
  #pendingSelectedVariantResource = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    interactedVariantPickers.add(this);

    const shouldNavigateToLinkedProduct =
      event.target.dataset.linkedProduct === 'true' && !!event.target.dataset.connectedProductUrl;

    if (shouldNavigateToLinkedProduct) {
      const currentPath = this.dataset.productUrl ? new URL(this.dataset.productUrl, window.location.origin).pathname : '';
      const nextUrl = new URL(event.target.dataset.connectedProductUrl, window.location.origin);

      if (nextUrl.pathname !== currentPath) {
        window.location.href = nextUrl.href;
      }
      return;
    }

    this.updateSelectedOption(event.target);

    this.#pendingSelectedVariantResource = this.#buildSelectedVariantResourceFromTarget(event.target);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: event.target.dataset.optionValueId ?? '',
        variantId: this.#pendingSelectedVariantResource?.id ?? '',
        available: this.#pendingSelectedVariantResource?.available,
        title: this.#pendingSelectedVariantResource?.title ?? '',
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId = this.#pendingSelectedVariantResource?.id ? String(this.#pendingSelectedVariantResource.id) : null;

    if (!variantId && event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (!variantId && event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElements = Array.from(this.querySelectorAll(`[data-option-value-id="${target}"]`));

      if (!targetElements.length) throw new Error('Target element not found');

      targetElements.forEach((targetElement) => {
        this.updateSelectedOption(targetElement);
      });
      return;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent =
          html.querySelector(`variant-picker script[type="application/json"][data-selected-variant]`)?.textContent ??
          html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          const selectedVariant = this.#buildSelectedVariantResource(JSON.parse(textContent));

          this.#pendingSelectedVariantResource = null;

          this.dispatchEvent(
            new VariantUpdateEvent(selectedVariant, this.selectedOptionId ?? '', {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.#selectedControls[0];

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const selectedOption = this.#selectedVariantControl;
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption.dataset.optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    return this.#selectedVariantControls
      .map((option) => option.dataset.optionValueId)
      .filter((optionValueId) => typeof optionValueId === 'string' && optionValueId.length > 0);
  }

  get #selectedControls() {
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], .variant-option input[type="radio"]:checked')
    );

    return selectedOptions.filter((option) => {
      let container;

      if (option instanceof HTMLOptionElement) {
        container = option.parentElement?.closest('.variant-option');
      } else if (option instanceof HTMLElement) {
        container = option.closest('.variant-option');
      }

      if (!(container instanceof HTMLElement)) return true;

      return window.getComputedStyle(container).display !== 'none';
    });
  }

  get #selectedVariantControls() {
    return this.#selectedControls.filter((option) => option.dataset.linkedProduct !== 'true');
  }

  /**
   * Gets the selected variant resource based on the current controls.
   * Falls back to the rendered variant payload only when it matches the actual selected variant.
   * @param {Object | null} renderedVariant
   * @returns {Object | null}
   */
  #buildSelectedVariantResource(renderedVariant) {
    if (this.#pendingSelectedVariantResource?.id) {
      if (renderedVariant && String(renderedVariant.id) === String(this.#pendingSelectedVariantResource.id)) {
        return {
          ...renderedVariant,
          ...this.#pendingSelectedVariantResource,
          available: this.#pendingSelectedVariantResource.available ?? renderedVariant.available,
          title: this.#pendingSelectedVariantResource.title || renderedVariant.title,
        };
      }

      return this.#pendingSelectedVariantResource;
    }

    const selectedVariantId = this.selectedVariantId;
    const selectedVariantTitle = this.selectedVariantTitle;
    const selectedVariantAvailable = this.selectedVariantAvailable;

    if (!selectedVariantId) {
      return renderedVariant ?? null;
    }

    if (renderedVariant && String(renderedVariant.id) === selectedVariantId) {
      return {
        ...renderedVariant,
        available: selectedVariantAvailable ?? renderedVariant.available,
        title: selectedVariantTitle || renderedVariant.title,
      };
    }

    return {
      id: selectedVariantId,
      available: selectedVariantAvailable ?? false,
      title: selectedVariantTitle,
    };
  }

  /**
   * Gets the selected variant ID.
   * @returns {string | undefined}
   */
  get selectedVariantId() {
    const exactVariant = this.#selectedVariantFromOptions;
    if (exactVariant?.id) return String(exactVariant.id);

    const selectedVariant = this.#selectedVariantControl;

    if (!(selectedVariant instanceof HTMLInputElement || selectedVariant instanceof HTMLOptionElement)) {
      return undefined;
    }

    const variantId = selectedVariant.dataset.variantId;

    return typeof variantId === 'string' && variantId.length > 0 ? variantId : undefined;
  }

  /**
   * Gets whether the selected variant is available.
   * @returns {boolean | undefined}
   */
  get selectedVariantAvailable() {
    const exactVariant = this.#selectedVariantFromOptions;
    if (typeof exactVariant?.available === 'boolean') return exactVariant.available;

    const selectedVariant = this.#selectedVariantControl;

    if (
      (selectedVariant instanceof HTMLInputElement || selectedVariant instanceof HTMLOptionElement) &&
      selectedVariant.dataset.optionAvailable === 'true'
    ) {
      return true;
    }

    if (
      (selectedVariant instanceof HTMLInputElement || selectedVariant instanceof HTMLOptionElement) &&
      selectedVariant.dataset.optionAvailable === 'false'
    ) {
      return false;
    }

    const availabilityValues = this.#selectedVariantControls
      .map((option) => option.dataset.optionAvailable)
      .filter((availability) => availability === 'true' || availability === 'false');

    if (!availabilityValues.length) return undefined;

    return availabilityValues.every((availability) => availability === 'true');
  }

  /**
   * Gets the selected variant title from the visible controls.
   * @returns {string}
   */
  get selectedVariantTitle() {
    const selectedVariant = this.#selectedVariantFromOptions;
    if (selectedVariant?.title) return selectedVariant.title;

    return this.#selectedVariantControls
      .map((option) => {
        if (option instanceof HTMLOptionElement) {
          return option.dataset.optionLabel ?? option.label ?? option.value;
        }

        return option.dataset.optionLabel ?? option.value;
      })
      .filter((value) => typeof value === 'string' && value.length > 0)
      .join(' / ');
  }

  /**
   * Builds the selected variant resource from the interacted control.
   * @param {HTMLElement} target
   * @returns {Object | null}
   */
  #buildSelectedVariantResourceFromTarget(target) {
    const selectedVariant = this.#selectedVariantFromOptions;

    if (selectedVariant) return selectedVariant;

    const selectedVariantId = this.#getTargetVariantId(target) ?? this.selectedVariantId;

    if (!selectedVariantId) return null;

    const targetAvailability = this.#getTargetAvailability(target);

    return {
      id: selectedVariantId,
      available: targetAvailability ?? this.selectedVariantAvailable ?? false,
      title: this.selectedVariantTitle,
    };
  }

  /**
   * Gets the availability value from the interacted control.
   * @param {HTMLElement} target
   * @returns {boolean | undefined}
   */
  #getTargetAvailability(target) {
    if (target instanceof HTMLInputElement) {
      if (target.dataset.optionAvailable === 'true') return true;
      if (target.dataset.optionAvailable === 'false') return false;
      return undefined;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];

      if (selectedOption?.dataset.optionAvailable === 'true') return true;
      if (selectedOption?.dataset.optionAvailable === 'false') return false;
    }

    return undefined;
  }

  /**
   * Gets the variant ID from the interacted control.
   * @param {HTMLElement} target
   * @returns {string | undefined}
   */
  #getTargetVariantId(target) {
    if (target instanceof HTMLInputElement) {
      const variantId = target.dataset.variantId;
      return typeof variantId === 'string' && variantId.length > 0 ? variantId : undefined;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      const variantId = selectedOption?.dataset.variantId;
      return typeof variantId === 'string' && variantId.length > 0 ? variantId : undefined;
    }

    return undefined;
  }

  /**
   * Gets the exact selected variant by matching the selected option labels against the product variants payload.
   * @returns {Object | null}
   */
  get #selectedVariantFromOptions() {
    const selectedOptions = this.#selectedVariantControls
      .map((option) => {
        const value = this.#getSelectedControlLabel(option);
        const position = Number(option.dataset.inputId?.split('-')[0]);

        return {
          value,
          position: Number.isFinite(position) ? position - 1 : undefined,
        };
      })
      .filter((option) => typeof option.value === 'string' && option.value.length > 0);

    if (!selectedOptions.length) return null;

    return (
      this.#productVariants.find((variant) => {
        const variantOptions = Array.isArray(variant.options)
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);

        return selectedOptions.every(({ value, position }, index) => {
          const optionIndex = typeof position === 'number' ? position : index;

          return String(variantOptions[optionIndex]) === value;
        });
      }) ?? null
    );
  }

  /**
   * Gets all product variants embedded in the picker.
   * @returns {Object[]}
   */
  get #productVariants() {
    const variantsJson = this.querySelector('script[type="application/json"][data-product-variants]')?.textContent;
    if (!variantsJson) return [];

    try {
      const variants = JSON.parse(variantsJson);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * Gets the display value for a selected variant control.
   * @param {HTMLInputElement | HTMLOptionElement} option
   * @returns {string}
   */
  #getSelectedControlLabel(option) {
    if (option instanceof HTMLOptionElement) {
      return option.dataset.optionLabel ?? option.label ?? option.value;
    }

    return option.dataset.optionLabel ?? option.value;
  }

  /**
   * Gets the selected control that best represents the chosen variant.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get #selectedVariantControl() {
    const controlsWithVariantId = this.#selectedVariantControls.filter((option) => {
      const variantId = option.dataset.variantId;
      return typeof variantId === 'string' && variantId.length > 0;
    });

    const selectedVariant =
      controlsWithVariantId[controlsWithVariantId.length - 1] ??
      this.#selectedVariantControls[this.#selectedVariantControls.length - 1];

    if (!(selectedVariant instanceof HTMLInputElement || selectedVariant instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedVariant;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
